import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Loading, ErrorState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Send, ArrowLeft, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";

const asList = (d) => (Array.isArray(d) ? d : d?.items || d?.messages || d?.results || []);
const DAYS = [
  { key: "0", label: "Lunedì" },
  { key: "1", label: "Martedì" },
  { key: "2", label: "Mercoledì" },
  { key: "3", label: "Giovedì" },
  { key: "4", label: "Venerdì" },
  { key: "5", label: "Sabato" },
  { key: "6", label: "Domenica" },
];

export default function Chat() {
  const { user } = useAuth();
  const [otherId] = useState(
    user?.physio_id || user?.therapist_id || user?.assigned_physio_id || ""
  );
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [physio, setPhysio] = useState(null);
  const [showHours, setShowHours] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async (id) => {
    if (!id) return;
    setLoading(true); setError("");
    try { setMessages(asList(await api.conversation(id))); }
    catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (otherId) load(otherId); }, [otherId, load]);
  useEffect(() => { api.physioAvailability().then(setPhysio).catch(() => {}); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || !otherId) return;
    setSending(true);
    try {
      await api.sendMessage({ receiver_id: otherId, recipient_id: otherId, to_user_id: otherId, content: text, text });
      setText(""); load(otherId);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSending(false); }
  };

  const avail = physio?.availability || {};
  const hasHours = DAYS.some((d) => avail[d.key]?.enabled);

  return (
    <div>
      <Card className="flex h-[78vh] flex-col overflow-hidden border-border/70">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border/60 bg-white px-5 py-4">
          <Avatar className="h-11 w-11 bg-primary/15">
            {physio?.physio_logo ? <AvatarImage src={physio.physio_logo} alt="fisioterapista" /> : null}
            <AvatarFallback className="bg-transparent text-sm font-semibold text-primary">{(physio?.physio_name || "F")[0].toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <p className="text-lg font-bold text-[#1f3a5f]">{physio?.physio_name || "Il tuo fisioterapista"}</p>
            <p className="text-xs text-muted-foreground">Fisioterapista</p>
          </div>
          {hasHours && (
            <Button variant="ghost" size="sm" onClick={() => setShowHours((v) => !v)} data-testid="chat-hours-toggle">
              <Clock className="mr-1 h-4 w-4" /> Orari
            </Button>
          )}
        </div>

        {/* Reception hours */}
        {showHours && hasHours && (
          <div className="border-b border-border/60 bg-[#f7fafc] px-5 py-3 text-sm" data-testid="chat-physio-hours">
            <p className="mb-1 font-semibold text-[#1f3a5f]">Orari di ricevimento in chat</p>
            {DAYS.filter((d) => avail[d.key]?.enabled).map((d) => (
              <p key={d.key} className="text-muted-foreground"><span className="font-medium text-foreground">{d.label}:</span> {avail[d.key].start} – {avail[d.key].end}</p>
            ))}
          </div>
        )}

        {/* Emergency banner */}
        <div className="flex items-center justify-center gap-2 bg-[#fdf3d9] px-4 py-2 text-sm font-medium text-[#3f7a70]">
          <AlertTriangle className="h-4 w-4 text-[#d9a441]" /> Non usare la chat per emergenze mediche
        </div>

        {!otherId ? (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="chat-no-physio">
            Nessun fisioterapista associato al tuo profilo. Contatta il supporto.
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-3 overflow-y-auto bg-[#f2f6fb] p-4" data-testid="chat-messages">
              {loading ? <Loading label="Caricamento messaggi..." /> : error ? <ErrorState message={error} onRetry={() => load(otherId)} /> :
                messages.length === 0 ? <p className="pt-10 text-center text-sm text-muted-foreground">Nessun messaggio. Scrivi il primo!</p> :
                  messages.map((m, i) => {
                    const mine = (m.sender_id || m.from_user_id || m.sender) === user.id;
                    return (
                      <div key={m.id || m._id || i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${mine ? "rounded-br-md bg-primary text-primary-foreground" : "rounded-bl-md bg-white text-foreground"}`}>
                          <p className="whitespace-pre-line">{m.content || m.text || m.message}</p>
                          <p className={`mt-1 text-[10px] ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                            {new Date(m.created_at || m.timestamp || Date.now()).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
              <div ref={bottomRef} />
            </div>
            <div className="flex items-center gap-2 border-t border-border/60 bg-white p-3">
              <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Scrivi un messaggio..." className="rounded-full" data-testid="chat-input" />
              <Button onClick={send} disabled={sending} size="icon" className="h-11 w-11 shrink-0 rounded-full" data-testid="chat-send-button"><Send className="h-4 w-4" /></Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
